import { AbstractControl, ValidatorFn } from "@angular/forms";

/**
 * Validadores específicos para CONTRIBUTION.PER.UY_CREDIT.CREDIT_DEFAULT y
 * CONTRIBUTION.EMP.UY_CREDIT.CREDIT_DEFAULT.
 * Todos son "solo lectura" respecto al valor: nunca modifican lo que el usuario escribió,
 * únicamente marcan el control como inválido para deshabilitar el guardado.
 */

function parseNumericValue(rawValue: any): number | null {
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return null;
  }
  const numericValue = parseFloat(String(rawValue).replace(",", "."));
  return isNaN(numericValue) ? null : numericValue;
}

/**
 * La fecha de operación debe ser igual o anterior a la fecha de incumplimiento.
 * Se aplica sobre el control de fecha_operacion, recibiendo el control de fecha_incumplimiento.
 */
export const operationDateNotAfterDefaultValidator = (defaultDateControl: AbstractControl): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const operationValue = control.value;
    const defaultValue = defaultDateControl?.value;

    if (!operationValue || !defaultValue) {
      return null;
    }

    const operationDate = new Date(operationValue);
    const defaultDate = new Date(defaultValue);

    if (isNaN(operationDate.getTime()) || isNaN(defaultDate.getTime())) {
      return null;
    }

    const result = operationDate.getTime() > defaultDate.getTime() ? { fechaoperacionmayor: true } : null;
    return result;
  };
};

/**
 * No permite comas ni puntos en el valor (bloquea el ingreso de centésimos), sin
 * alterar el texto ingresado.
 */
export const noDecimalSeparatorValidator = (): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value;
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const result = /[.,]/.test(String(value)) ? { decimalnopermitido: true } : null;
    return result;
  };
};

/**
 * Saldo deudor con mínimo dependiente de la moneda de incumplimiento:
 * "$" (Pesos Uruguayos) -> mínimo 1000, "US$" (Dólares Americanos) -> mínimo 25.
 * El resto de monedas no tiene mínimo.
 */
export const saldoDeudorMinByCurrencyValidator = (monedaControl: AbstractControl): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const numericValue = parseNumericValue(control.value);
    if (numericValue === null) {
      return null;
    }

    const moneda = String(monedaControl?.value ?? "").trim();
    let min: number | null = null;
    if (moneda === "$") {
      min = 1000;
    } else if (moneda === "US$") {
      min = 25;
    }

    if (min === null) {
      return null;
    }

    const result = numericValue < min ? { saldominimo: { min } } : null;
    return result;
  };
};

/**
 * El control no debe superar el valor numérico de otro control. Si el otro control
 * es nulo/vacío o no numérico, no se aplica ninguna restricción.
 */
export const maxAgainstFieldValidator = (otherControl: AbstractControl): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const otherNumericValue = parseNumericValue(otherControl?.value);
    if (otherNumericValue === null) {
      return null;
    }

    const numericValue = parseNumericValue(control.value);
    if (numericValue === null) {
      return null;
    }

    const result = numericValue > otherNumericValue ? { saldomayormonto: true } : null;
    return result;
  };
};

